import React, { useCallback, useMemo } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';
import { updatePageloadName } from '@atlaskit/react-ufo/trace-pageload';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment from '@atlaskit/react-ufo/segment';

import { SearchPage } from '@atlassian/search-page/ui';
import { EnterpriseFeedbackComparisonPage } from '@atlassian/enterprise-feedback-comparison';

import { expVal } from '@confluence/feature-experiments';
import { TOUCH } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { UserContext } from '@confluence/navigation-search-dialog/entry-points/UserContext.ts';
import { usePreloadDataProvider } from '@confluence/search-route-preloader/entry-points/usePreloadDataProvider.ts';
import { fg } from '@confluence/feature-gating';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { useGetNavigateToTeamsApp } from '@confluence/teams-app-config/entry-points/useGetNavigateToTeamsApp.ts';
import { AISmartButtonWrapper } from '@confluence/ai-rovo-button/entry-points/AISmartButtonWrapper.ts';

import { useGetSpaceIdsFromSpaceKeys } from './useGetSpaceIdsFromSpaceKeys.tsx';
import { PageLoadEndEventUnifiedSearch } from './PageLoadEndEvent.tsx';

// TODO: Part of this list is supported filter query keys
// We can have those keys exporeted from search-page
// so when we add a new filter, we don't need to add it in two places
export const SEARCH_PAGE_QUERY_PARAM_KEYS = [
	'product',
	'text',
	'originalQuery', // This is used by "Did you mean" search correction feature
	'type',
	'lastModified',
	// "lastModified_lt",
	// "lastModified_gt",
	'titleMatch',
	'createdBy',
	'lastUpdatedBy',
	'contributors',
	'owners',
	'spaces',
	'pageStatus',
	'verified',
	'ancestor',
	'labels',
	'jAssignee',
	'jReporter',
	'jProject',
	'jCommentedByMe',
	'jWatchedByMe',
	'focusAreaTypes',
	'focusAreaUnder',
	'referrer',
	'leftOverrides',
	'rightOverrides',
	'from',
	'to',
	'cql',
	'archived',
	'filterDropdownOpen',
	'datasource',
	'status',
	'compassArchived',
	'assetsSchema',
	'assetsType',
	'salesforceStages',
	'campaignStatuses',
	'caseStatuses',
	'closeDate',
	'closeDateVal',
	'positionFocusAreas',
	'positionEmploymentTypes',
	'positionJobFamilies',
	'positionStatuses',
	'positionTeams',
	'isAutoApplied',
	'sort',
	'overrides',
	'opportunityStatus',
	'pullRequestStatus',
	'slackChannels',
] as const;

const enableSideBySideQueryParams = ['leftOverrides', 'rightOverrides'];

const containerStyles = xcss({
	marginLeft: 'space.negative.025',
});

const searchPageWithButtonWrapperStyles = xcss({
	position: 'relative',
	width: '100%',
});

const aiButtonPositionStyles = xcss({
	position: 'absolute',
	insetBlockEnd: 'space.300',
	insetInlineEnd: 'space.300',
});

type QueryParamEntry = [string, string | undefined];

const createSearchQueryParamsHook = (queryParamKeys: readonly string[]) => {
	const queryParamHooks = queryParamKeys.map(
		(paramKey) =>
			[paramKey, createSingleQueryParamHook(paramKey)] as [
				string,
				ReturnType<typeof createSingleQueryParamHook>,
			],
	);

	return (): Record<string, any> => {
		const queryParamEntries = queryParamHooks
			.map(([paramKey, hook]) => [paramKey, hook()])
			.filter(([_, paramValue]) => !!paramValue);

		return Object.fromEntries(queryParamEntries);
	};
};

const getSearchQueryParams = createSearchQueryParamsHook(SEARCH_PAGE_QUERY_PARAM_KEYS);

const InnerSearchPage = ({ userEmail }: { userEmail?: string }) => {
	const {
		cloudId,
		userId,
		displayName,
		orgId,
		activationId,
		isAdminHubAIEnabled,
		isRovoLLMEnabled,
	} = useSessionData();
	const { push, setQueryParams } = useRouteActions();
	const setQueryParamsCallback = useCallback(
		(params, replace: boolean = false) => {
			const filteredQueryParams = SEARCH_PAGE_QUERY_PARAM_KEYS.map(
				(paramKey): QueryParamEntry => [paramKey, params[paramKey]],
			);

			setQueryParams(Object.fromEntries(filteredQueryParams), replace);
		},
		[setQueryParams],
	);

	interface UserDetails {
		id: string | null;
		name: string | null;
		email?: string | null;
	}

	const userDetails = useMemo<UserDetails>(() => {
		return {
			id: userId,
			name: displayName,
			email: userEmail,
		};
	}, [userId, displayName, userEmail]);

	const onNavigate = useCallback((url: string) => push(url), [push]);

	const queryParams = getSearchQueryParams();

	const getSpaceIdsFromSpaceKeys = useGetSpaceIdsFromSpaceKeys({
		setQueryParamsCallback,
		queryParams,
	});

	const getNavigateToTeamsApp = useGetNavigateToTeamsApp();
	const mapUserIdToProfileUrl = (id: string) => {
		if (fg('confluence-adopt-teams-nav-config')) {
			return getNavigateToTeamsApp({
				type: 'USER',
				payload: {
					userId: id,
				},
			}).href;
		} else {
			return `/wiki/people/${id}`;
		}
	};

	const experimentLayers = StatsigConfigurations.getStringList(
		'search_platform_layer_functions',
		[],
	);
	const preloadDataProvider = usePreloadDataProvider();

	return enableSideBySideQueryParams.some((key) => key in queryParams) &&
		fg('enable_rovo_search_overrides') ? (
		<Box xcss={containerStyles}>
			<EnterpriseFeedbackComparisonPage
				callbacks={{ updateQueryParameters: setQueryParamsCallback, getSpaceIdsFromSpaceKeys }}
				shell={{
					cloudId,
					orgId,
					activationId,
					user: userDetails,
					primaryProduct: 'confluence',
					queryParams,
					isAdminHubAIEnabled,
					isNav4Enabled: true, //nav4 is rolled out, search platform can be cleaned up
					experimentLayers,
					isRovoLLMEnabled,
				}}
				mapUserIdToProfileUrl={mapUserIdToProfileUrl}
				onNavigate={onNavigate}
				testId="enterprise-feedback-comparison"
				entryPoint="confluence"
			/>
		</Box>
	) : (
		<Box xcss={containerStyles}>
			{/* âš ï¸ if the props change on this component, please change EnterpriseFeedbackComparisonPage as well - it uses this component under the hood âš ï¸*/}
			<Box xcss={searchPageWithButtonWrapperStyles}>
				<SearchPage
					callbacks={{ updateQueryParameters: setQueryParamsCallback, getSpaceIdsFromSpaceKeys }}
					shell={{
						cloudId,
						orgId,
						user: userDetails,
						primaryProduct: 'confluence',
						analyticsAttributes: { activationId, navdexPointType: TOUCH },
						queryParams,
						isAdminHubAIEnabled,
						isNav4Enabled: true, //nav4 is rolled out, search platform can be cleaned up
						experimentLayers,
						isRovoLLMEnabled,
						preloadDataProvider,
					}}
					mapUserIdToProfileUrl={mapUserIdToProfileUrl}
					onNavigate={onNavigate}
				/>
				{(expVal('failed_search_journey_trigger_rovo_chat', 'cohort', 'control') !== 'control' ||
					expVal('failed_search_journey_trigger_rovo_chat', 'cohort', 'control') !==
						'not-enrolled') && (
					<Box xcss={aiButtonPositionStyles}>
						<AISmartButtonWrapper
							contentId=""
							contentType=""
							pageMode="advanced-search"
							isDisabled={false}
						/>
					</Box>
				)}
			</Box>
			<PageLoadEndEventUnifiedSearch />
		</Box>
	);
};

export const ConfluenceSearchPage = () => {
	// update advanced search page load name (used for UFO)
	useMemo(() => updatePageloadName('unified_search'), []);

	return (
		<UFOSegment name="advanced-search-search-page">
			<UserContext>
				{({ data, isLoading }) =>
					!isLoading ? (
						<InnerSearchPage userEmail={data?.email} />
					) : (
						<UFOLoadHold name="search-page-user-context-loading" />
					)
				}
			</UserContext>
		</UFOSegment>
	);
};
